import { useMemo } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Badge } from '../ui/badge';
import { type Campaign } from './types';
import { CampaignTooltipContent } from './CampaignTooltipContent';
import { formatDateShort, getDayOfYear, getTotalDaysInYear, getCurrentDatePosition, splitCampaignByYear } from './utils';
import { MONTHS } from './constants';

interface CampaignTimelineProps {
  campaigns: Campaign[];
    selectedYear: string;
    collapsedUnits: Record<string, boolean>;
}

interface CampaignWithPosition {
  startMonth: number;
    endMonth: number;
    campaign: Campaign;
}

interface CampaignWithRow extends CampaignWithPosition {
    row: number;
}

function assignCampaignRows(campaigns: CampaignWithPosition[]): {
    campaignsWithRows: CampaignWithRow[];
    rowCount: number;
} {
    const sorted = [...campaigns].sort((a, b) => a.startMonth - b.startMonth || a.endMonth - b.endMonth);
    const rows: { start: number; end: number }[][] = [];
 const result: CampaignWithRow[] = [];
    
    for (const c of sorted) {
        let placed = false;
        for (let i = 0; i < rows.length; i++) {
            if (!rows[i].some(r => !(c.endMonth < r.start || c.startMonth > r.end))) {
    rows[i].push({ start: c.startMonth, end: c.endMonth });
  result.push({ ...c, row: i });
        placed = true;
    break;
   }
        }
        if (!placed) {
            rows.push([{ start: c.startMonth, end: c.endMonth }]);
     result.push({ ...c, row: rows.length - 1 });
        }
    }
    
    return { campaignsWithRows: result, rowCount: rows.length };
}

export function CampaignTimeline({ campaigns, selectedYear, collapsedUnits }: CampaignTimelineProps) {
    const timelineCampaignsByBusinessUnit = useMemo(() => {
  const result: Record<string, CampaignWithPosition[]> = {};
        
   for (const campaign of campaigns) {
     const split = splitCampaignByYear(campaign, parseInt(selectedYear));
      if (!split) continue;
            
     if (!result[campaign.businessUnit]) {
      result[campaign.businessUnit] = [];
    }
            result[campaign.businessUnit].push({ ...split, campaign });
 }
        
     return result;
    }, [campaigns, selectedYear]);

    const groupedAndOrderedCampaigns = useMemo(() => {
  const grouped: Record<string, Campaign[]> = {};
   for (const campaign of campaigns) {
            if (!grouped[campaign.businessUnit]) grouped[campaign.businessUnit] = [];
    grouped[campaign.businessUnit].push(campaign);
  }
  const orderedUnits = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    return orderedUnits.map(unit => ({ businessUnit: unit, campaigns: grouped[unit] }));
    }, [campaigns]);

    const businessUnitRowHeights = useMemo(() => {
   const heights: Record<string, number> = {};
  for (const { businessUnit } of groupedAndOrderedCampaigns) {
 const unitCampaigns = timelineCampaignsByBusinessUnit[businessUnit] || [];
     const { rowCount } = assignCampaignRows(unitCampaigns);
   const rowHeight = 44;
   heights[businessUnit] = Math.max(rowCount * rowHeight, 64);
        }
     return heights;
    }, [groupedAndOrderedCampaigns, timelineCampaignsByBusinessUnit]);

 return (
  <div className="space-y-6">
   <div className="flex items-center justify-between">
       <h3 className="font-medium">Campaign Timeline {selectedYear}</h3>
 <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
   <div className="w-3 h-3 rounded-full bg-blue-500"></div>
     <span className="text-gray-600">Upcoming</span>
       </div>
    <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
         <span className="text-gray-600">Active</span>
  </div>
      <div className="flex items-center gap-2">
  <div className="w-3 h-3 rounded-full bg-gray-500"></div>
       <span className="text-gray-600">Completed</span>
     </div>
  </div>
        </div>
       
     <div className="bg-white rounded-lg border overflow-hidden">
   <div className="flex">
       {/* Left sidebar with business units */}
    <div className="w-48 bg-gray-50 border-r">
     <div className="p-4 border-b bg-gray-100 h-12 flex items-center">
    <h4 className="font-medium text-sm text-gray-700">Business Units</h4>
        </div>
           <div className="divide-y">
       {groupedAndOrderedCampaigns.map(({ businessUnit }) => {
         const unitCampaigns = timelineCampaignsByBusinessUnit[businessUnit] || [];
       const isCollapsed = collapsedUnits[businessUnit];
    if (isCollapsed || unitCampaigns.length === 0) return null;
    const height = businessUnitRowHeights[businessUnit] || 64;
 const activeCampaigns = unitCampaigns.filter(c => c.campaign.status === 'active').length;
      const upcomingCampaigns = unitCampaigns.filter(c => c.campaign.status === 'upcoming').length;
         const completedCampaigns = unitCampaigns.filter(c => c.campaign.status === 'completed').length;
   
     return (
        <div key={businessUnit} className="p-4 flex items-center border-b last:border-b-0" style={{ height }}>
       <div className="flex-1">
         <div className="flex items-center justify-between mb-1">
  <h5 className="font-medium text-sm flex-1">{businessUnit}</h5>
       <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
             {unitCampaigns.length}
   </span>
      </div>
        <div className="flex gap-4 text-xs mt-1">
      {completedCampaigns > 0 && (
   <div className="flex items-center gap-2">
     <div className="w-2 h-2 rounded-full bg-gray-500"></div>
    <span className="text-gray-600">Completed</span>
<span className="text-gray-500">{completedCampaigns}</span>
      </div>
         )}
         {activeCampaigns > 0 && (
 <div className="flex items-center gap-2">
    <div className="w-2 h-2 rounded-full bg-green-500"></div>
    <span className="text-gray-600">Active</span>
      <span className="text-gray-500">{activeCampaigns}</span>
        </div>
  )}
     {upcomingCampaigns > 0 && (
     <div className="flex items-center gap-2">
      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
        <span className="text-gray-600">Upcoming</span>
           <span className="text-gray-500">{upcomingCampaigns}</span>
           </div>
            )}
       </div>
     </div>
   </div>
     );
      })}
    </div>
    </div>

        {/* Timeline area */}
       <div className="flex-1 min-w-0">
 <div className="border-b bg-gray-50 h-12 flex items-center">
     <div className="grid grid-cols-12 gap-0 w-full">
  {MONTHS.map((month) => (
     <div key={month} className="text-center text-xs font-medium text-gray-600 border-r last:border-r-0 flex items-center justify-center h-full">
    {month}
       </div>
       ))}
   </div>
 </div>

  <div className="relative">
    {/* Current date indicator */}
      <div
   className="absolute top-0 bottom-0 w-0.5 bg-blue-600 z-10"
       style={{ left: `${getCurrentDatePosition()}%` }}
    >
   <div className="absolute -top-2 -left-1 w-2 h-2 bg-blue-600 rounded-full"></div>
      </div>
       
      {/* Month dividers */}
           <div className="absolute inset-0 grid grid-cols-12 gap-0 pointer-events-none">
   {MONTHS.map((_, index) => (
      <div key={index} className="border-r border-gray-100 last:border-r-0 h-full"></div>
     ))}
      </div>

       {/* Campaign bars */}
    <div className="space-y-0">
    {groupedAndOrderedCampaigns.map(({ businessUnit }) => {
         const unitCampaigns = timelineCampaignsByBusinessUnit[businessUnit] || [];
     const isCollapsed = collapsedUnits[businessUnit];
  if (isCollapsed || unitCampaigns.length === 0) return null;
      
    const { campaignsWithRows } = assignCampaignRows(unitCampaigns);
    const rowHeight = 44;
        const timelineHeight = businessUnitRowHeights[businessUnit] || 64;
       const totalDays = getTotalDaysInYear(parseInt(selectedYear));
       
    return (
      <div key={businessUnit} className="border-b border-gray-100 last:border-b-0 relative" style={{ minHeight: 64, height: timelineHeight }}>
     {campaignsWithRows.map(({ campaign, row }) => {
     const startDate = new Date(campaign.startDate);
     const endDate = new Date(campaign.endDate);
     const startDay = getDayOfYear(startDate);
    const endDay = getDayOfYear(endDate);
         const left = ((startDay - 1) / totalDays) * 100;
    const width = ((endDay - startDay + 1) / totalDays) * 100;
         
     return (
        <Tooltip key={`${campaign.id}-${startDay}-${endDay}`}>
      <TooltipTrigger asChild>
       <div
        className={`absolute h-10 rounded-md flex items-center px-3 text-xs font-medium shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
    campaign.status === 'upcoming' ? 'bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200' :
           campaign.status === 'active' ? 'bg-green-100 text-green-800 border border-green-200 hover:bg-green-200' :
   campaign.status === 'completed' ? 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200' :
  'bg-yellow-100 text-yellow-800 border border-yellow-200 hover:bg-yellow-200'
    }`}
       style={{
        left: `${left}%`,
       width: `${Math.max(width, 0.5)}%`,
       top: row * rowHeight
      }}
        >
    <div className="flex items-center gap-2 min-w-0">
         <span className="truncate font-medium">{campaign.name}</span>
        <span className="text-xs opacity-75">
    {formatDateShort(campaign.startDate)}
   </span>
      <Badge variant="outline" className="text-xs px-1 py-0 h-4">
     {campaign.campaignType || (campaign.status === 'active' ? 'Lead Capturing' : 'Awareness')}
     </Badge>
      </div>
         </div>
           </TooltipTrigger>
    <TooltipContent side="top" className="p-0 border-0 bg-transparent shadow-lg">
     <div className="bg-white border rounded-lg shadow-lg">
     <CampaignTooltipContent campaign={campaign} />
    </div>
     </TooltipContent>
   </Tooltip>
         );
        })}
      </div>
           );
       })}
     </div>
        </div>
       </div>
      </div>

            {groupedAndOrderedCampaigns.length === 0 && (
              <div className="text-center py-12 text-gray-500">
  <p>No campaigns found for the selected filters.</p>
       <p className="text-center text-sm mt-1">Try adjusting your filters or create a new campaign.</p>
         </div>
   )}
          </div>
        </div>
    );
}
